'use client';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, Trash2 } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import type { Reminder } from '@shared/types';
import {
  useCreateReminder,
  useDeleteReminder,
  useReminders,
} from '@/hooks/useReminders';
import { useTimePrefs } from '@/hooks/useTimePrefs';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { formatSmartDateTime } from '@/lib/time-format';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';

// Header bell that opens a small panel with a quick-add input + the
// user's upcoming + recently-fired reminders. Standalone reminders are
// separate from tasks; this is the only surface that shows them.
export function RemindersMenu() {
  const { data } = useReminders();
  const reminders = data ?? [];
  const upcomingCount = reminders.filter((r) => !r.firedAt || r.recurringType !== null).length;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Reminders"
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition duration-fast ease-out-quint hover:bg-surface-muted hover:text-foreground"
        >
          <Bell className="h-4 w-4" strokeWidth={2} />
          {upcomingCount > 0 && (
            <span
              aria-hidden
              className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-accent shadow-glow"
            />
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={10}
          className="z-50 w-[22rem] max-w-[calc(100vw-2rem)] space-y-3 rounded-xl border border-hairline bg-surface/90 p-4 shadow-lg backdrop-blur-md animate-fade-in"
        >
          <header className="space-y-0.5">
            <p className="eyebrow eyebrow-strong">Reminders</p>
            <p className="text-xs text-muted-foreground">
              Quick nudges. Recurring or one-shot — separate from your tasks.
            </p>
          </header>

          <QuickAdd />

          {reminders.length > 0 && (
            <>
              <div className="hairline" />
              <ul className="max-h-72 space-y-1 overflow-y-auto">
                {reminders.map((r) => (
                  <RemindersListItem key={r.id} reminder={r} />
                ))}
              </ul>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function QuickAdd() {
  const create = useCreateReminder();
  const [message, setMessage] = useState('');
  const [remindAt, setRemindAt] = useState<string | null>(defaultRemindAt());

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!message.trim() || !remindAt || create.isPending) return;
    create.mutate(
      { message: message.trim(), remindAt },
      {
        onSuccess: () => {
          setMessage('');
          setRemindAt(defaultRemindAt());
        },
        onError: () => toast.error("Couldn't add the reminder"),
      },
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <input
        type="text"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Switch the laundry…"
        maxLength={500}
        className="w-full rounded-lg border border-border bg-surface-sunken/60 px-3 py-2 text-sm outline-none transition duration-fast ease-out-quint placeholder:text-subtle-foreground focus:border-accent"
      />
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <DateTimePicker value={remindAt} onChange={setRemindAt} placeholder="When" />
        </div>
        <button
          type="submit"
          disabled={!message.trim() || !remindAt || create.isPending}
          className="rounded-full bg-accent px-4 py-2 text-xs font-semibold text-primary-foreground transition duration-fast ease-out-quint hover:shadow-glow hover:brightness-105 disabled:opacity-40"
        >
          {create.isPending ? 'Adding…' : 'Add'}
        </button>
      </div>
    </form>
  );
}

interface ListItemProps {
  reminder: Reminder;
}

function RemindersListItem({ reminder }: ListItemProps) {
  const del = useDeleteReminder();
  const timePrefs = useTimePrefs();
  const isPending = !reminder.firedAt;
  const isRecurring = reminder.recurringType !== null;

  // Closure on the time prefs result — fine because the dropdown
  // re-renders whenever the underlying settings change.
  const when = useMemo(
    () =>
      formatSmartDateTime(new Date(reminder.remindAt), timePrefs, { withDate: 'always' }),
    [reminder.remindAt, timePrefs],
  );

  return (
    <li
      className={cn(
        'group flex items-start gap-3 rounded-lg px-2 py-2 transition duration-fast ease-out-quint hover:bg-surface-muted',
        !isPending && !isRecurring && 'opacity-60',
      )}
    >
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="truncate text-sm leading-tight">{reminder.message}</p>
        <p className="font-mono text-[10px] tabular-nums text-subtle-foreground">
          {when}
          {isRecurring && (
            <span className="ml-1 uppercase tracking-eyebrow">· {reminder.recurringType}</span>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={() =>
          del.mutate(reminder.id, {
            onError: () => toast.error("Couldn't delete the reminder"),
          })
        }
        disabled={del.isPending}
        aria-label="Delete reminder"
        className="shrink-0 rounded-lg p-1 text-subtle-foreground opacity-0 transition duration-fast ease-out-quint hover:bg-surface hover:text-priority-urgent focus:opacity-100 group-hover:opacity-100 disabled:opacity-50"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

// Default to one hour from now, rounded down to the minute. Most quick
// reminders are within a few hours so this is a sane starting point.
function defaultRemindAt(): string {
  const d = new Date();
  d.setHours(d.getHours() + 1);
  d.setSeconds(0, 0);
  return d.toISOString();
}
